import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Logo from '../common/Logo';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);

      // Track active section based on scroll position
      if (location.pathname === '/') {
        const sections = ['products', 'about', 'contact'];
        const headerHeight = window.innerWidth >= 768 ? 96 : 80;
        const scrollPosition = window.scrollY + headerHeight + 50; // Reduced offset for better detection

        let currentSection = null;

        // Check if we're in the hero section (near the top)
        const heroElement = document.getElementById('hero');
        const heroBottom = heroElement ? heroElement.offsetTop + heroElement.offsetHeight : window.innerHeight;

        // Only check other sections if we've scrolled past the hero section
        if (window.scrollY > heroBottom - 200) { // Start checking before fully past hero
          for (const sectionId of sections) {
            const element = document.getElementById(sectionId);
            if (element) {
              const elementTop = element.offsetTop;
              const elementBottom = elementTop + element.offsetHeight;

              if (scrollPosition >= elementTop && scrollPosition < elementBottom) {
                currentSection = sectionId;
                break;
              }
            }
          }

          // If scrolled past all sections, set the last one as active
          if (!currentSection && sections.length > 0) {
            const lastSection = document.getElementById(sections[sections.length - 1]);
            if (lastSection && scrollPosition >= lastSection.offsetTop) {
              currentSection = sections[sections.length - 1];
            }
          }
        }
        // If we're in the hero section (scrollPosition <= heroBottom), currentSection stays null
        // which means Home will be active

        setActiveSection(currentSection);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Call once to set initial state

    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname === '/') {
      const hash = window.location.hash;
      if (hash === '#products' || hash === '#contact' || hash === '#about') {
        const sectionId = hash.substring(1);
        setActiveSection(sectionId);
        // Wait for page to fully load before scrolling
        setTimeout(() => {
          const element = document.getElementById(sectionId);
          if (element) {
            const headerHeight = window.innerWidth >= 768 ? 96 : 80; // md:h-24 = 96px, h-20 = 80px
            const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
            const offsetPosition = elementPosition - headerHeight;

            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          }
        }, 300);
      } else if (!hash || hash === '') {
        // No hash means we're at the top/home
        setActiveSection(null);
      }
    }
  }, [location]);

  // Handle hash changes (browser back/forward)
  useEffect(() => {
    const handleHashChange = () => {
      if (location.pathname === '/') {
        const hash = window.location.hash;
        if (hash === '#products' || hash === '#contact' || hash === '#about') {
          const sectionId = hash.substring(1);
          setActiveSection(sectionId);
        } else {
          setActiveSection(null);
        }
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [location.pathname]);

  const navLinks = [
    { path: '/', label: 'Home', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6', scrollTo: null },
    { path: '/', label: 'About', icon: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z', scrollTo: 'about' },
    { path: '/', label: 'Products', icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10', scrollTo: 'products' },
    { path: '/', label: 'Contact', icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z', scrollTo: 'contact' }
  ];

  const handleNavClick = (e, link) => {
    if (link.scrollTo) {
      e.preventDefault();
      if (location.pathname !== '/') {
        // If not on homepage, navigate first then scroll
        window.location.href = `/#${link.scrollTo}`;
      } else {
        // If on homepage, just scroll with offset for header
        const element = document.getElementById(link.scrollTo);
        if (element) {
          const headerHeight = window.innerWidth >= 768 ? 96 : 80; // md:h-24 = 96px, h-20 = 80px
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - headerHeight;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });

          // Update URL hash
          window.history.pushState(null, '', `#${link.scrollTo}`);

          // Update active section
          setActiveSection(link.scrollTo);
        }
      }
    } else {
      // Home link - scroll to top
      e.preventDefault();
      if (location.pathname !== '/') {
        window.location.href = '/';
      } else {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });

        // Clear URL hash
        window.history.pushState(null, '', '/');

        // Clear active section (Home will be active)
        setActiveSection(null);
      }
    }
  };

  const isActive = (link) => {
    if (link.scrollTo) {
      return activeSection === link.scrollTo;
    }
    // Home is active only when we're at the top (no active section) and on homepage
    return location.pathname === link.path && activeSection === null;
  };

  return (
    <header className={`sticky top-0 z-50 transition-all duration-500 ${isScrolled
        ? 'bg-white/98 backdrop-blur-xl shadow-xl border-b border-amber-100/50'
        : 'bg-white/90 backdrop-blur-sm shadow-md'
      }`}>
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-amber-50/30 via-transparent to-orange-50/30 pointer-events-none"></div>

      <nav className="container mx-auto px-4 md:px-6 lg:px-8 relative">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Logo */}
          <div className="flex items-center flex-shrink-0 z-10 max-w-[120px] sm:max-w-[140px] md:max-w-[180px] lg:max-w-none">
            <Link 
              to="/" 
              className="flex items-center group relative w-full"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-amber-400/20 to-orange-400/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative transform group-hover:scale-105 transition-transform duration-300 w-full">
                <Logo size="small" variant="dark" />
              </div>
            </Link>
          </div>

          {/* Social Media Links - Center */}
          <div className="hidden lg:flex items-center space-x-3 absolute left-1/2 transform -translate-x-1/2 z-10">
            <a
              href="https://www.instagram.com/wovnrugss"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative w-10 h-10 rounded-xl flex items-center justify-center text-gray-700 hover:text-amber-700 hover:bg-amber-50/60 transition-all duration-300"
              aria-label="Follow us on Instagram"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
              <div className="absolute inset-0 bg-gradient-to-r from-amber-400/10 to-orange-400/10 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </a>
            <a
              href="https://www.linkedin.com/company/wovnrugss/"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative w-10 h-10 rounded-xl flex items-center justify-center text-gray-700 hover:text-amber-700 hover:bg-amber-50/60 transition-all duration-300"
              aria-label="Follow us on LinkedIn"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
              <div className="absolute inset-0 bg-gradient-to-r from-amber-400/10 to-orange-400/10 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </a>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.scrollTo ? `${link.path}#${link.scrollTo}` : link.path}
                onClick={(e) => handleNavClick(e, link)}
                className={`group relative px-4 py-2.5 rounded-xl font-semibold text-sm transition-all duration-300 ${isActive(link)
                    ? 'text-amber-700 bg-gradient-to-br from-amber-50 to-orange-50 shadow-md'
                    : 'text-gray-700 hover:text-amber-700 hover:bg-amber-50/60'
                  }`}
              >
                <span className="relative z-10 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={link.icon} />
                  </svg>
                  {link.label}
                </span>
                {isActive(link) && (
                  <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-8 h-0.5 bg-gradient-to-r from-amber-600 to-orange-600 rounded-full"></span>
                )}
                <div className="absolute inset-0 bg-gradient-to-r from-amber-400/10 to-orange-400/10 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-3 rounded-xl text-gray-700 hover:bg-gradient-to-br hover:from-amber-50 hover:to-orange-50 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 transition-all duration-300 relative z-10"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            <div className="relative w-6 h-6">
              <span className={`absolute top-0 left-0 w-6 h-0.5 bg-gray-700 rounded-full transition-all duration-300 ${isMenuOpen ? 'rotate-45 top-2.5' : ''
                }`}></span>
              <span className={`absolute top-2.5 left-0 w-6 h-0.5 bg-gray-700 rounded-full transition-all duration-300 ${isMenuOpen ? 'opacity-0' : 'opacity-100'
                }`}></span>
              <span className={`absolute top-5 left-0 w-6 h-0.5 bg-gray-700 rounded-full transition-all duration-300 ${isMenuOpen ? '-rotate-45 top-2.5' : ''
                }`}></span>
            </div>
          </button>
        </div>

        {/* Mobile Menu */}
        <div className={`lg:hidden overflow-hidden transition-all duration-500 ease-out ${isMenuOpen ? 'max-h-[500px] opacity-100 mt-4' : 'max-h-0 opacity-0'
          }`}>
          <div className="py-6 space-y-3 border-t-2 border-gradient-to-r from-amber-200 to-orange-200 bg-gradient-to-br from-white to-amber-50/30 rounded-2xl mt-2 shadow-xl">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.scrollTo ? `${link.path}#${link.scrollTo}` : link.path}
                onClick={(e) => {
                  handleNavClick(e, link);
                  setIsMenuOpen(false);
                }}
                className={`group relative block px-6 py-4 mx-2 rounded-xl font-semibold transition-all duration-300 ${isActive(link)
                    ? 'text-amber-700 bg-gradient-to-r from-amber-100 to-orange-100 shadow-lg border-l-4 border-amber-600'
                    : 'text-gray-700 hover:text-amber-700 hover:bg-gradient-to-r hover:from-amber-50 hover:to-orange-50'
                  }`}
              >
                <span className="flex items-center gap-3">
                  <svg className={`w-5 h-5 ${isActive(link) ? 'text-amber-600' : 'text-gray-500 group-hover:text-amber-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={link.icon} />
                  </svg>
                  {link.label}
                </span>
                {isActive(link) && (
                  <div className="absolute right-4 top-1/2 transform -translate-y-1/2 w-2 h-2 bg-amber-600 rounded-full"></div>
                )}
              </Link>
            ))}
            {/* Instagram Link - Mobile */}
            <a
              href="https://www.instagram.com/wovnrugss"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative block px-6 py-4 mx-2 rounded-xl font-semibold transition-all duration-300 text-gray-700 hover:text-amber-700 hover:bg-gradient-to-r hover:from-amber-50 hover:to-orange-50"
              onClick={() => setIsMenuOpen(false)}
            >
              <span className="flex items-center gap-3">
                <svg className="w-5 h-5 text-gray-500 group-hover:text-amber-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
                <span>Instagram</span>
              </span>
            </a>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
